import { InviteUserBody, Profile, UpdateUserBody, User } from '@bioliberty/lifemind';
import { TokenizedPagedList } from './responses';
import { tms } from './tms';

export type UpdateUserParams = Omit<UpdateUserBody, 'enabled'> & {
  id: string;
  enabled?: boolean;
};

const users = tms.injectEndpoints({
  endpoints: (builder) => ({
    getProfile: builder.query<Profile, void>({
      query: () => `profile`,
      transformResponse: ({ subtenants, ...profile }: Profile): Profile => ({
        ...profile,
        subtenants: subtenants?.map((subtenant) => ({ ...subtenant, inactive: Boolean(subtenant.inactive) }))
      }),
      providesTags: ['Profile']
    }),

    getUsers: builder.query<TokenizedPagedList<User>, { pageToken?: string }>({
      query: ({ pageToken }) => {
        const query = pageToken ? `?page=${encodeURIComponent(pageToken)}` : '';
        return `users${query}`;
      },
      providesTags: ['Users']
    }),

    getUsersByIds: builder.query<User[], string[]>({
      query: (ids) => `users?ids=${ids.map(encodeURIComponent).join(',')}`,
      transformResponse: (response: { items: User[] }) => response.items,
      providesTags: ['Users']
    }),

    inviteUser: builder.mutation<User, InviteUserBody>({
      query: (userInvite) => ({
        url: `users`,
        method: 'POST',
        body: userInvite
      }),
      invalidatesTags: ['Users']
    }),

    updateUser: builder.mutation<void, UpdateUserParams>({
      query: (userUpdate) => ({
        url: `users/${userUpdate.id}`,
        method: 'PUT',
        body: userUpdate
      }),
      invalidatesTags: ['Users', 'Profile']
    }),

    deleteUser: builder.mutation<void, string>({
      query: (id) => ({
        url: `users/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Users']
    })
  }),
  overrideExisting: false
});

export const {
  useGetProfileQuery,
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useGetUsersByIdsQuery,
  useInviteUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation
} = users;
